import { useSynchronizedSelection } from '../../../../core/components/shared-chart-interactions/hooks/useSynchronizedSelection.js';
import { getZoomStep } from '../../../../core/components/zoom/utils/get-zoom-step.js';
import type { AnyNumberScale } from '../../../../core/types/scales.js';
import { ZOOM_CHANGE_TYPE } from '../../../../core/types/zoom.js';
import { isSameDomain } from '../../../../core/utils/zoom.js';
import { useXYChartData } from '../../../hooks/useXYChartData.js';
import { useXYChartScales } from '../../../hooks/useXYChartScales.js';
import { reduceZoomDomain } from '../../../store/reducer-zoom.js';
import { useSelectState, useSetState } from '../../../store/selectors.js';
import type { XYChartState } from '../../../types/state.js';
import {
  isInfiniteZoomEnabledFor,
  isZoomEnabledFor,
} from '../../../utils/axis-interactions.js';
import { getCurrentScale, getInitialScale } from '../../../utils/get-scale.js';
import { handleZoomChange } from '../utils/zoom-change-handler.js';

export const useZoomToSelection = () => {
  const setState = useSetState();
  const axisIdToScales = useXYChartScales();
  const { axes } = useXYChartData();
  const select = useSelectState();
  const { resetSelectionState } = useSynchronizedSelection();

  let zoomInEnabled = false;
  let zoomOutEnabled = false;

  return () => {
    const newCurrentDomain: XYChartState['currentDomain'] = new Map();

    if (!select) {
      return;
    }

    for (const axis of axes) {
      if (isZoomEnabledFor(axis)) {
        const currentScale = getCurrentScale(axisIdToScales, axis.id);
        const initialScale = getInitialScale(axisIdToScales, axis.id);

        const initialDomain = initialScale.domain() as number[] | Date[];

        const {
          nextDomain,
          zoomInEnabled: inEnabled,
          zoomOutEnabled: outEnabled,
        } = getZoomStep(
          currentScale as AnyNumberScale,
          [+initialDomain[0], +initialDomain[1]],
          isInfiniteZoomEnabledFor(axis),
          select.start.relative.x,
          select.end.relative.x,
          'zoom-x',
        );

        zoomInEnabled ||= inEnabled;
        zoomOutEnabled ||= outEnabled;

        newCurrentDomain.set(
          axis.id,
          nextDomain
            ? { min: nextDomain[0].valueOf(), max: nextDomain[1].valueOf() }
            : undefined,
        );

        const selectionCurrentDomain = currentScale.domain() as (
          | number
          | Date
        )[];
        if (
          !isSameDomain(nextDomain as (number | Date)[], selectionCurrentDomain)
        ) {
          handleZoomChange(
            axis,
            nextDomain[0],
            nextDomain[1],
            ZOOM_CHANGE_TYPE.ZoomX,
          );
        }
      }
    }
    resetSelectionState?.();
    setState(reduceZoomDomain(newCurrentDomain, zoomInEnabled, zoomOutEnabled));
  };
};
